import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ThemeService } from '../../service/theme.service';
import { IotService } from '../../service/iot.service';
import { BinsService } from '../../service/bins.service';
import { ClientService } from '../../service/client.service';
import { DistributorsService } from '../../service/distributors.service';
import { ModalService } from '../../service/device-modal.service';
import { IotDevicesViewService } from '../iot-devices-view.service';
import { S3Service } from '../../service/s3.service';
import { SystemMessageService } from '../../service/system-message.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-iot-technical-table',
  templateUrl: './iot-technical-table.component.html',
  styleUrls: ['./iot-technical-table.component.css'],
  standalone: false,
})
export class IotTechnicalTableComponent implements OnInit {
  showStateInfo: boolean[] = [];

  constructor(
    public theme: ThemeService,
    public iotService: IotService,
    public view: IotDevicesViewService,
    public modal: ModalService,
    private binService: BinsService,
    private s3: S3Service,
    private systemMessage: SystemMessageService,
    private clientService: ClientService,
    private distributorService: DistributorsService,
    private router: Router,
    public translate: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.view.init();
    this.showStateInfo = new Array(this.iotService.devicesArray.length).fill(
      false
    );
  }

  formatNumber(val: any) {
    return val !== null && val !== undefined ? val : 'N/A';
  }

  verifyStatus(thingName: string): string {
    return this.iotService.checkHealthStatusUsingThingName
      ? this.iotService.checkHealthStatusUsingThingName(thingName)
      : 'Inactive';
  }

  heartbeatClass(deviceRow: any): string {
    const color = deviceRow?.heartbeat?.color;
    if (color === 'green') return 'light-green';
    if (color === 'yellow') return 'light-yellow';
    if (color === 'red') return 'light-red';
    return '';
  }

  setStateInfo(index: number, value: boolean) {
    this.showStateInfo[index] = value;
  }

  // Function used to refresh device data initialized for production
  async initializeDeviceForProduction(thingName: string) {
    const confirm = window.confirm(
      await firstValueFrom(this.translate.get('initializeDevice'))
    );

    // Wait to user to select confirm on the window message
    if (confirm) {
      try {
        const initialized: any = await firstValueFrom(
          this.iotService.initializeDeviceForProduction(thingName)
        );

        const shadowUpdate = await this.iotService.updateThingShadow(thingName);
        if (initialized.message === 'success') {
          this.systemMessage.selectRibbon('success', 'initializeWithSuccess');
        }
      } catch (error) {
        console.error('Error', error);
        this.systemMessage.selectRibbon(
          'danger',
          'alert-danger-generic-message'
        );
      }
    }
  }

  showMap(device: any) {
    // Map overlay lives in the devices table; keep this as a no-op to avoid errors
    console.warn('Map view is available from the Devices tab.');
  }

  async showDeviceNotes(thingName: string) {
    await this.iotService.getDeviceNotes(thingName);
    this.modal.deviceNotes = this.iotService.deviceNotes;
    this.modal.showLoadingStatus = false;
    this.modal.showDeviceModal = true;
    this.modal.showNotesModal = true;
  }

  async showRelation(thingName: string) {
    await firstValueFrom(
      await this.iotService.getDeviceRelationship(thingName)
    );
    this.modal.deviceAssociationRow = this.iotService.deviceAssociationRow;

    let array: any = await this.distributorService.getDistributorForSelect();
    this.modal.distributorsArray = array;

    array = await this.clientService.getClientsForSelect();
    this.modal.clientsArray = array;

    const res: any = await firstValueFrom(this.binService.getBinsJson());
    if (res) {
      this.modal.binArray = res;
      this.modal.binArray = this.modal.binArray.filter(
        (bin: any) => bin.thing_name === ''
      );
    }

    this.modal.thingName = thingName;
    this.modal.showDeviceModal = true;
    this.modal.showRelationship = true;
  }

  setNavToDeviceStat(thingName: string) {
    sessionStorage.setItem('toDeviceStats', 'iot/technical');
    this.router.navigate([`/device-statistics/${thingName}`]);
  }

  async getCertificateFolderUrl(thingName: string) {
    await this.s3.getCertificatesForThing(thingName).then((res) => {
      if (res?.length) {
        const cert = res[0];
        const a = document.createElement('a');
        a.href = cert;
        a.download = `${thingName}-certificates.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        this.systemMessage.selectRibbon(
          'danger',
          'noCertificatesSystemMessage'
        );
      }
    });
  }
}
